import React, { useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import en from "../../language/Eng_Aust.json";

interface RecommendationItem {
  id: number;
  tag: string;
  image: string;
  title: string;
  price: string;
  description: string;
  meta?: string;
}

const recommendationData: RecommendationItem[] = en.home.recommendation.items;

const formatPrice = (price: string) =>
  price === "00.00" ? "$00.00" : `$${Number(price).toFixed(2)}`;

const Buttered_Recommendation: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [loadedImages, setLoadedImages] = useState<{ [key: number]: boolean }>({});

  const handleImageLoad = (id: number) => {
    setLoadedImages((prev) => ({ ...prev, [id]: true }));
  };

  const handleScroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 344;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <SectionContainer>
      <HeaderWrapper>
        <div>
          <MainTitle>{en.home.recommendation.title}</MainTitle>
          <SubDescription>{en.home.recommendation.description}</SubDescription>
        </div>
        <HeaderActions><ViewAll to="/mainmenu">{en.home.recommendation.viewAll} <ChevronRight size={16} /></ViewAll><NavButtons>
          <NavButton aria-label={en.home.recommendation.previousAriaLabel} onClick={() => handleScroll("left")}>
            <ChevronLeft size={24} />
          </NavButton>
          <NavButton aria-label={en.home.recommendation.nextAriaLabel} onClick={() => handleScroll("right")}>
            <ChevronRight size={24} />
          </NavButton>
        </NavButtons></HeaderActions>
      </HeaderWrapper>

      <ScrollWrapper ref={scrollRef}>
        <SliderContainer>
          {recommendationData.map((item) => (
            <Card key={item.id}>
              <ImageArea>
                {item.tag && <Badge>{item.tag}</Badge>}
                {!loadedImages[item.id] && <Skeleton />}
                <StyledImage
                  src={item.image}
                  alt={item.title}
                  onLoad={() => handleImageLoad(item.id)}
                  $isLoaded={loadedImages[item.id] || false}
                  loading="lazy"
                />
              </ImageArea>
              <ContentArea>
                <TitleRow>
                  <MenuTitle>{item.title}</MenuTitle>
                  <Price>{formatPrice(item.price)}</Price>
                </TitleRow>
                {item.description && <Description>{item.description}</Description>}
              </ContentArea>
            </Card>
          ))}
        </SliderContainer>
      </ScrollWrapper>
    </SectionContainer>
  );
};

export default Buttered_Recommendation;

const SectionContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 30px auto 60px;
  padding: 0;
  width: 90%;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  @media (max-width: 768px) {
    margin: 20px auto 40px;
    padding: 0;
    width: calc(100% - 32px);
  }
`;

const HeaderWrapper = styled.div`
  margin-bottom: 22px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const MainTitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: #333;
  margin-bottom: 8px;
`;

const SubDescription = styled.p`
  font-size: 1rem;
  color: #888;
  margin: 0;
`;

const NavButtons = styled.div`
  display: flex;
  gap: 12px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid #eee;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #333;
  &:hover {
    background: #f9f9f9;
    border-color: #ddd;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }
`;

const ScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding-bottom: 20px;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const SliderContainer = styled.div`
  display: flex;
  gap: 24px;
  scroll-snap-type: x mandatory;
`;

const Card = styled.div`
  flex: 0 0 320px;
  scroll-snap-align: start;
  background: white;
  border-radius: 28px;
  padding: 12px;
  box-sizing: border-box;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  @media (max-width: 480px) {
    flex: 0 0 76%;
  }
`;

const ImageArea = styled.div`
  position: relative;
  width: 100%;
  height: 220px;
  border-radius: 20px;
  @media(max-width: 480px) { height: 190px; }
  background-color: #f5f5f5;
  overflow: hidden;
`;

const skeletonPulse = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const Skeleton = styled.div`
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: #e0e0e0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    animation: ${skeletonPulse} 1.5s infinite linear;
  }
`;

const StyledImage = styled.img<{ $isLoaded?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
`;

const Badge = styled.span`
  position: absolute;
  top: 20px;
  left: 20px;
  background-color: #fde047;
  color: #333;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 28px;
  padding: 12px;
  box-sizing: border-box;
  text-transform: uppercase;
  z-index: 1;
`;

const ContentArea = styled.div`
  padding: 14px 4px 4px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const MenuTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: #222;
  margin: 0;
  word-break: keep-all;
  line-height: 1.3;
`;

const Price = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: #51402d;
  text-align: left;
  word-break: keep-all;
  line-height: 1.3;
`;

const Description = styled.p`
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
  margin: 10px 0 0;
`;

const HeaderActions = styled.div`display: flex; align-items: center; gap: 20px;`;
const ViewAll = styled(Link)`display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; color: #342e28; font-size: 13px; font-weight: 650; text-decoration: none; min-height: 44px;`;
